import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Button from '../common/Button';
import GradientButton from '../common/GradientButton';
import Input from '../common/Input';
import JalaliDatePicker from '../common/JalaliDatePicker';
import Modal from '../common/Modal';
import FileUpload from '../common/FileUpload';
import Select from '../common/Select';
import {
  sanitizeNationalCode,
  isValidNationalCode,
  getNationalCodeError,
} from '../../utils/nationalCode';

import {
  AGE_GROUP_LABELS,
  GENDER_LABELS,
  PREFERRED_FOOT_LABELS,
  PLAYER_LEVEL_LABELS,
  BLOOD_TYPE_LABELS,
  POSITIONS,
} from '../../utils/constants';

import {
  createPlayerApi,
  updatePlayerApi,
  uploadPlayerPhotoApi,
  uploadPlayerMedicalCertificateApi,
  lookupPlayerNationalCodeApi,
} from '../../api/player.api';

const emptyForm = {
  clubId: '',
  firstName: '',
  lastName: '',
  nationalCode: '',
  birthDate: '',
  ageGroup: 'NOJAVAN',
  gender: 'MALE',
  position: '',
  insuranceNumber: '',
  height: '',
  weight: '',
  preferredFoot: 'RIGHT',
  jerseyNumber: '',
  previousClubs: '',
  bio: '',
  phone: '',
  email: '',
  nationality: '',
  birthPlace: '',
  city: '',
  fatherName: '',
  bloodType: '',
  playerLevel: 'AMATEUR',
  mainPosition: '',
  secondPosition: '',
  marketValue: '',
  agentName: '',
  instagramUrl: '',
  telegramUrl: '',
};

const MAX_PHOTO_SIZE = 5 * 1024 * 1024;

function getPhotoUrl(photo) {
  if (!photo) return null;

  if (
    photo.startsWith('http://') ||
    photo.startsWith('https://') ||
    photo.startsWith('blob:')
  ) {
    return photo;
  }

  const baseURL =
    process.env.REACT_APP_API_BASE_URL?.replace(/\/api\/v1\/?$/, '') ||
    window.location.origin;

  return `${baseURL}${photo.startsWith('/') ? '' : '/'}${photo}`;
}

export default function PlayerForm({
  open,
  onClose,
  onSaved,
  player,
  clubs,
  isAdmin,
}) {
  const [form, setForm] = useState(emptyForm);

  const [photoFile, setPhotoFile] = useState(null);
  const [certFile, setCertFile] = useState(null);

  const [photoPreview, setPhotoPreview] = useState(null);

  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);

  // وضعیت بررسی لحظه‌ای کد ملی: idle | invalid | checking | free | taken | self
  const [codeCheck, setCodeCheck] = useState({ status: 'idle', info: null });
  const [codeTouched, setCodeTouched] = useState(false);
  const [serverCodeError, setServerCodeError] = useState('');

  const photoInputRef = useRef(null);
  const objectUrlRef = useRef(null);

  const isEdit = Boolean(player);

  useEffect(() => {
    if (player) {
      setForm({
        clubId: player.clubId || '',
        firstName: player.firstName || '',
        lastName: player.lastName || '',
        nationalCode: player.nationalCode || '',
        birthDate: player.birthDate || '',
        ageGroup: player.ageGroup || 'NOJAVAN',
        gender: player.gender || 'MALE',
        position: player.position || '',
        insuranceNumber: player.insuranceNumber || '',
        height: player.height || '',
        weight: player.weight || '',
        preferredFoot: player.preferredFoot || 'RIGHT',
        jerseyNumber: player.jerseyNumber || '',
        previousClubs: player.previousClubs || '',
        bio: player.bio || '',
        phone: player.phone || '',
        email: player.email || '',
        nationality: player.nationality || '',
        birthPlace: player.birthPlace || '',
        city: player.city || '',
        fatherName: player.fatherName || '',
        bloodType: player.bloodType || '',
        playerLevel: player.playerLevel || 'AMATEUR',
        mainPosition: player.mainPosition || '',
        secondPosition: player.secondPosition || '',
        marketValue: player.marketValue || '',
        agentName: player.agentName || '',
        instagramUrl: player.instagramUrl || '',
        telegramUrl: player.telegramUrl || '',
      });

      setPhotoPreview(getPhotoUrl(player.photo));
    } else {
      setForm(emptyForm);
      setPhotoPreview(null);
    }

    setPhotoFile(null);
    setCertFile(null);
    setError('');
    setPhotoUploading(false);
    setCodeTouched(false);
    setServerCodeError('');
    setCodeCheck({ status: 'idle', info: null });

    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
  }, [player, open]);

  useEffect(() => {
    return () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
      }
    };
  }, []);

  // بررسی لحظه‌ای کد ملی (با کمی تأخیر تا برای هر کلید درخواست نرود)
  useEffect(() => {
    if (!open) return undefined;

    const code = sanitizeNationalCode(form.nationalCode);

    if (code.length !== 10) {
      setCodeCheck({ status: 'idle', info: null });
      return undefined;
    }
    if (!isValidNationalCode(code)) {
      setCodeCheck({ status: 'invalid', info: null });
      return undefined;
    }
    if (player?.nationalCode === code) {
      setCodeCheck({ status: 'self', info: null });
      return undefined;
    }

    let cancelled = false;
    setCodeCheck({ status: 'checking', info: null });

    const timer = setTimeout(async () => {
      try {
        const { data } = await lookupPlayerNationalCodeApi(code);
        if (cancelled) return;
        const result = data?.data;

        if (result?.exists && result.playerId !== player?.id) {
          setCodeCheck({ status: 'taken', info: result });
        } else {
          setCodeCheck({ status: 'free', info: null });
        }
      } catch {
        // اگر بررسی لحظه‌ای شکست خورد، سرور هنگام ثبت دوباره بررسی می‌کند
        if (!cancelled) setCodeCheck({ status: 'idle', info: null });
      }
    }, 350);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [form.nationalCode, open, player]);

  if (!open) return null;

  const handleNationalCodeChange = (e) => {
    setServerCodeError('');
    setForm((prev) => ({ ...prev, nationalCode: sanitizeNationalCode(e.target.value) }));
  };

  const handleChange = (e) => {
    setForm((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleDateChange = (date) => {
    setForm((prev) => ({
      ...prev,
      birthDate: date,
    }));
  };

  const handlePhotoSelect = (file) => {
    setError('');

    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('لطفاً یک فایل تصویری معتبر انتخاب کنید.');
      return;
    }

    if (file.size > MAX_PHOTO_SIZE) {
      setError('حجم تصویر نباید بیشتر از ۵ مگابایت باشد.');
      return;
    }

    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
    }

    const previewUrl = URL.createObjectURL(file);
    objectUrlRef.current = previewUrl;

    setPhotoFile(file);
    setPhotoPreview(previewUrl);
  };

  const handleFileChange = (file, type) => {
    if (type === 'photo') {
      handlePhotoSelect(file);
    } else {
      setCertFile(file);
    }
  };

  const removePhoto = () => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }

    setPhotoFile(null);
    setPhotoPreview(null);

    if (photoInputRef.current) {
      photoInputRef.current.value = '';
    }
  };

  const openPhotoPicker = () => {
    photoInputRef.current?.click();
  };

  const handlePhotoInputChange = (e) => {
    const file = e.target.files?.[0];

    if (file) {
      handlePhotoSelect(file);
    }

    e.target.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setError('');

    if (isAdmin && !isEdit && !form.clubId) {
      setError('انتخاب باشگاه برای مدیر کل الزامی است');
      return;
    }

    setCodeTouched(true);
    const codeError = getNationalCodeError(form.nationalCode);
    if (codeError) {
      setError(codeError);
      return;
    }
    if (codeCheck.status === 'taken') {
      setError('این کد ملی قبلاً برای بازیکن دیگری ثبت شده است؛ ثبت دوباره ممکن نیست.');
      return;
    }
    if (codeCheck.status === 'checking') {
      setError('در حال بررسی کد ملی هستیم؛ چند لحظه صبر کنید.');
      return;
    }

    setSubmitting(true);

    try {
      const payload = { ...form, nationalCode: sanitizeNationalCode(form.nationalCode) };

      if (!isAdmin || !payload.clubId) {
        delete payload.clubId;
      }

      if (payload.height) {
        payload.height = Number(payload.height);
      }

      if (payload.weight) {
        payload.weight = Number(payload.weight);
      }

      if (payload.jerseyNumber) {
        payload.jerseyNumber = Number(payload.jerseyNumber);
      }

      if (payload.marketValue) {
        payload.marketValue = Number(payload.marketValue);
      }

      if (payload.birthDate) {
        payload.birthDate =
          payload.birthDate instanceof Date
            ? payload.birthDate
            : new Date(payload.birthDate);
      }

      let response;

      if (isEdit) {
        response = await updatePlayerApi(player.id, payload);

        if (photoFile) {
          setPhotoUploading(true);
          await uploadPlayerPhotoApi(player.id, photoFile);
          setPhotoUploading(false);
        }

        if (certFile) {
          await uploadPlayerMedicalCertificateApi(player.id, certFile);
        }
      } else {
        response = await createPlayerApi(payload);

        const createdPlayerId =
          response?.data?.data?.id ||
          response?.data?.id;

        if (!createdPlayerId) {
          throw new Error('شناسه بازیکن ایجادشده دریافت نشد');
        }

        if (photoFile) {
          setPhotoUploading(true);
          await uploadPlayerPhotoApi(createdPlayerId, photoFile);
          setPhotoUploading(false);
        }

        if (certFile) {
          await uploadPlayerMedicalCertificateApi(
            createdPlayerId,
            certFile
          );
        }
      }

      await onSaved?.();
      onClose?.();
    } catch (err) {
      setPhotoUploading(false);

      const fieldErr = err?.response?.data?.errors?.find?.(
        (item) => item.field === 'nationalCode'
      );
      if (fieldErr) setServerCodeError(fieldErr.message);

      setError(
        err?.response?.data?.message ||
          err?.message ||
          'ثبت بازیکن ناموفق بود'
      );
    } finally {
      setSubmitting(false);
      setPhotoUploading(false);
    }
  };

  const playerInitials =
    `${form.firstName?.charAt(0) || ''}${form.lastName?.charAt(0) || ''}` ||
    '?';

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? 'ویرایش بازیکن' : 'افزودن بازیکن جدید'}
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        {error && (
          <div className="flex items-center gap-3 rounded-2xl border border-red-100 bg-red-50 p-3 text-sm font-medium text-red-600">
            <div className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-red-100">
              !
            </div>

            <span>{error}</span>
          </div>
        )}

        {/* ================= PHOTO ================= */}
        <div className="relative overflow-hidden rounded-3xl border border-emerald-100 bg-gradient-to-br from-emerald-50 via-white to-slate-50 p-5">
          <input
            ref={photoInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/jpg"
            className="hidden"
            onChange={handlePhotoInputChange}
          />

          <div className="flex flex-col items-center justify-center gap-4 sm:flex-row sm:justify-start">
            {/* Avatar */}
            <div className="relative shrink-0">
              <div className="h-28 w-28 overflow-hidden rounded-full border-4 border-white bg-gradient-to-br from-emerald-400 via-emerald-500 to-emerald-700 shadow-xl shadow-emerald-500/20 ring-1 ring-emerald-100">
                {photoPreview ? (
                  <img
                    src={photoPreview}
                    alt={`${form.firstName} ${form.lastName}`}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-3xl font-black text-white">
                    {playerInitials}
                  </div>
                )}
              </div>

              {photoPreview && (
                <button
                  type="button"
                  onClick={removePhoto}
                  className="absolute -right-1 -top-1 grid h-8 w-8 place-items-center rounded-full border-2 border-white bg-red-500 text-white shadow-lg transition hover:scale-110 hover:bg-red-600"
                  title="حذف تصویر"
                >
                  ×
                </button>
              )}

              <button
                type="button"
                onClick={openPhotoPicker}
                className="absolute -bottom-1 -left-1 grid h-9 w-9 place-items-center rounded-full border-2 border-white bg-emerald-500 text-white shadow-lg transition hover:scale-110 hover:bg-emerald-600"
                title="تغییر تصویر"
              >
                📷
              </button>
            </div>

            {/* Photo info */}
            <div className="text-center sm:text-right">
              <h3 className="font-black text-slate-800">
                تصویر بازیکن
              </h3>

              <p className="mt-1 text-xs leading-6 text-slate-500">
                تصویر پرتره بازیکن را انتخاب کنید.
                <br />
                فرمت JPG، PNG یا WebP — حداکثر ۵MB
              </p>

              <div className="mt-3 flex flex-wrap justify-center gap-2 sm:justify-start">
                <button
                  type="button"
                  onClick={openPhotoPicker}
                  className="rounded-xl bg-emerald-500 px-4 py-2 text-xs font-bold text-white shadow-sm transition hover:bg-emerald-600"
                >
                  {photoPreview ? 'تعویض تصویر' : 'انتخاب تصویر'}
                </button>

                {photoPreview && (
                  <button
                    type="button"
                    onClick={removePhoto}
                    className="rounded-xl bg-red-50 px-4 py-2 text-xs font-bold text-red-500 transition hover:bg-red-100"
                  >
                    حذف تصویر
                  </button>
                )}
              </div>

              {photoFile && (
                <p className="mt-2 max-w-xs truncate text-[10px] text-emerald-600">
                  {photoFile.name}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* ================= BASIC INFO ================= */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {isAdmin && !isEdit && (
            <Select
              label="باشگاه"
              name="clubId"
              value={form.clubId}
              onChange={handleChange}
              required
              className="col-span-2"
            >
              <option value="">انتخاب باشگاه</option>

              {(clubs || []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          )}

          <div className="col-span-2 mt-2 flex items-center gap-3">
            <span className="grid h-8 w-8 place-items-center rounded-xl bg-emerald-100 text-sm">🪪</span>
            <h4 className="text-sm font-black text-slate-700">اطلاعات هویتی</h4>
            <span className="h-px flex-1 bg-gradient-to-l from-transparent to-slate-200" />
          </div>

          <Input
            label="نام"
            name="firstName"
            value={form.firstName}
            onChange={handleChange}
            required
          />

          <Input
            label="نام خانوادگی"
            name="lastName"
            value={form.lastName}
            onChange={handleChange}
            required
          />

          <div className="col-span-2 space-y-2 md:col-span-1">
            <Input
              label="کد ملی"
              name="nationalCode"
              value={form.nationalCode}
              onChange={handleNationalCodeChange}
              onBlur={() => setCodeTouched(true)}
              required
              inputMode="numeric"
              autoComplete="off"
              maxLength={10}
              dir="ltr"
              placeholder="۱۰ رقم، مثلاً 0012345678"
              readOnly={isEdit && !isAdmin && Boolean(player?.nationalCode)}
              className="text-left font-mono tracking-widest"
              error={
                serverCodeError ||
                (codeCheck.status === 'invalid'
                  ? 'کد ملی وارد‌شده معتبر نیست (رقم کنترل اشتباه است)'
                  : codeTouched && !form.nationalCode
                  ? 'کد ملی الزامی است'
                  : codeTouched && form.nationalCode.length < 10
                  ? 'کد ملی باید دقیقاً ۱۰ رقم باشد'
                  : '')
              }
              hint={
                isEdit && !isAdmin && player?.nationalCode
                  ? 'کد ملی هویت بازیکن است و فقط توسط مدیر هیئت قابل تغییر است.'
                  : 'کد ملی شناسه‌ی یکتای بازیکن است و در کل سامانه فقط یک بار ثبت می‌شود.'
              }
            />

            {codeCheck.status === 'checking' && (
              <p className="text-xs text-slate-500">در حال بررسی کد ملی...</p>
            )}

            {codeCheck.status === 'free' && (
              <p className="text-xs font-bold text-emerald-600">✓ کد ملی معتبر است و تکراری نیست</p>
            )}
          </div>

          {codeCheck.status === 'taken' && codeCheck.info && (
            <div className="col-span-2 flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
              <div className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-amber-100 text-base">⚠</div>
              <div className="space-y-1 leading-7">
                <p className="font-black">
                  این کد ملی قبلاً ثبت شده است
                  {codeCheck.info.sameClub ? ' (در همین باشگاه)' : ''}
                </p>
                <p>
                  {codeCheck.info.playerName ? `«${codeCheck.info.playerName}» ` : 'بازیکنی با این کد ملی '}
                  هم‌اکنون عضو باشگاه «{codeCheck.info.clubName || '—'}» است. هر بازیکن هم‌زمان فقط در یک باشگاه می‌تواند باشد؛
                  برای تغییر باشگاه باید «نقل و انتقال» ثبت شود.
                </p>
                {isAdmin ? (
                  <Link
                    to="/admin/transfers"
                    state={{ nationalCode: codeCheck.info.nationalCode }}
                    className="inline-block rounded-xl bg-amber-500 px-3 py-1.5 text-xs font-bold text-white transition hover:bg-amber-600"
                  >
                    ثبت نقل و انتقال این بازیکن
                  </Link>
                ) : (
                  !codeCheck.info.sameClub && (
                    <Link
                      to="/club/transfer-requests"
                      state={{ nationalCode: codeCheck.info.nationalCode }}
                      className="inline-block rounded-xl bg-amber-500 px-3 py-1.5 text-xs font-bold text-white transition hover:bg-amber-600"
                    >
                      ثبت درخواست انتقال این بازیکن
                    </Link>
                  )
                )}
              </div>
            </div>
          )}

          <JalaliDatePicker
            label="تاریخ تولد"
            value={form.birthDate}
            onChange={handleDateChange}
            required
          />

          <Select
            label="گروه سنی"
            name="ageGroup"
            value={form.ageGroup}
            onChange={handleChange}
            required
          >
            {Object.entries(AGE_GROUP_LABELS).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </Select>

          <Select
            label="جنسیت"
            name="gender"
            value={form.gender}
            onChange={handleChange}
            required
          >
            {Object.entries(GENDER_LABELS).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </Select>

          <div className="col-span-2 mt-2 flex items-center gap-3">
            <span className="grid h-8 w-8 place-items-center rounded-xl bg-emerald-100 text-sm">⚽</span>
            <h4 className="text-sm font-black text-slate-700">مشخصات ورزشی</h4>
            <span className="h-px flex-1 bg-gradient-to-l from-transparent to-slate-200" />
          </div>

          {/* ===== پست بازی (کامبوباکس) ===== */}
          <div>
            <label className="mb-1.5 block text-sm font-bold text-slate-700">پست بازی</label>
            <select
              name="position"
              value={form.position}
              onChange={handleChange}
              className="w-full rounded-xl border-2 border-slate-200 p-3 text-sm outline-none transition-all duration-300 focus:border-emerald-500 focus:shadow-lg focus:shadow-emerald-500/20"
            >
              <option value="">انتخاب پست</option>
              {POSITIONS.map((p) => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
          </div>

          <Input
            label="شماره بیمه"
            name="insuranceNumber"
            value={form.insuranceNumber}
            onChange={handleChange}
          />

          <Input
            label="قد (سانتی‌متر)"
            type="number"
            name="height"
            value={form.height}
            onChange={handleChange}
          />

          <Input
            label="وزن (کیلوگرم)"
            type="number"
            name="weight"
            value={form.weight}
            onChange={handleChange}
          />

          <Select
            label="پای برتر"
            name="preferredFoot"
            value={form.preferredFoot}
            onChange={handleChange}
          >
            {Object.entries(PREFERRED_FOOT_LABELS).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </Select>

          <Input
            label="شماره پیراهن"
            type="number"
            name="jerseyNumber"
            value={form.jerseyNumber}
            onChange={handleChange}
          />

          <Input
            label="باشگاه‌های قبلی"
            name="previousClubs"
            value={form.previousClubs}
            onChange={handleChange}
          />

          <Input
            label="بیوگرافی"
            name="bio"
            value={form.bio}
            onChange={handleChange}
          />

          <div className="col-span-2 mt-2 flex items-center gap-3">
            <span className="grid h-8 w-8 place-items-center rounded-xl bg-emerald-100 text-sm">📇</span>
            <h4 className="text-sm font-black text-slate-700">اطلاعات تماس و شخصی</h4>
            <span className="h-px flex-1 bg-gradient-to-l from-transparent to-slate-200" />
          </div>

          <Input
            label="تلفن"
            name="phone"
            value={form.phone}
            onChange={handleChange}
          />

          <Input
            label="ایمیل"
            type="email"
            name="email"
            value={form.email}
            onChange={handleChange}
          />

          <Input
            label="ملیت"
            name="nationality"
            value={form.nationality}
            onChange={handleChange}
          />

          <Input
            label="محل تولد"
            name="birthPlace"
            value={form.birthPlace}
            onChange={handleChange}
          />

          <Input
            label="شهر"
            name="city"
            value={form.city}
            onChange={handleChange}
          />

          <Input
            label="نام پدر"
            name="fatherName"
            value={form.fatherName}
            onChange={handleChange}
          />

          <Select
            label="گروه خونی"
            name="bloodType"
            value={form.bloodType}
            onChange={handleChange}
          >
            <option value="">گروه خونی</option>

            {Object.entries(BLOOD_TYPE_LABELS).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </Select>

          <Select
            label="سطح بازیکن"
            name="playerLevel"
            value={form.playerLevel}
            onChange={handleChange}
          >
            {Object.entries(PLAYER_LEVEL_LABELS).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </Select>

          {/* ===== پست اصلی (کامبوباکس) ===== */}
          <div>
            <label className="mb-1.5 block text-sm font-bold text-slate-700">پست اصلی</label>
            <select
              name="mainPosition"
              value={form.mainPosition}
              onChange={handleChange}
              className="w-full rounded-xl border-2 border-slate-200 p-3 text-sm outline-none transition-all duration-300 focus:border-emerald-500 focus:shadow-lg focus:shadow-emerald-500/20"
            >
              <option value="">انتخاب پست اصلی</option>
              {POSITIONS.map((p) => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
          </div>

          {/* ===== پست دوم (کامبوباکس) ===== */}
          <div>
            <label className="mb-1.5 block text-sm font-bold text-slate-700">پست دوم</label>
            <select
              name="secondPosition"
              value={form.secondPosition}
              onChange={handleChange}
              className="w-full rounded-xl border-2 border-slate-200 p-3 text-sm outline-none transition-all duration-300 focus:border-emerald-500 focus:shadow-lg focus:shadow-emerald-500/20"
            >
              <option value="">انتخاب پست دوم</option>
              {POSITIONS.map((p) => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
          </div>

          <Input
            label="ارزش بازار (تومان)"
            type="number"
            name="marketValue"
            value={form.marketValue}
            onChange={handleChange}
          />

          <Input
            label="نام نماینده"
            name="agentName"
            value={form.agentName}
            onChange={handleChange}
          />

          <div className="col-span-2 mt-2 flex items-center gap-3">
            <span className="grid h-8 w-8 place-items-center rounded-xl bg-emerald-100 text-sm">🌐</span>
            <h4 className="text-sm font-black text-slate-700">شبکه‌های اجتماعی</h4>
            <span className="h-px flex-1 bg-gradient-to-l from-transparent to-slate-200" />
          </div>

          <Input
            label="اینستاگرام"
            name="instagramUrl"
            value={form.instagramUrl}
            onChange={handleChange}
          />

          <Input
            label="تلگرام"
            name="telegramUrl"
            value={form.telegramUrl}
            onChange={handleChange}
          />
        </div>

        {/* ================= FILES ================= */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <FileUpload
            label="مدرک پزشکی"
            accept="image/*,application/pdf"
            onFileSelect={(file) =>
              handleFileChange(file, 'cert')
            }
          />
        </div>

        {/* ================= ACTIONS ================= */}
        <div className="flex justify-end gap-2 border-t border-slate-100 pt-4">
          <Button
            variant="ghost"
            onClick={onClose}
            type="button"
            disabled={submitting}
          >
            انصراف
          </Button>

          <GradientButton
            type="submit"
            disabled={submitting || codeCheck.status === 'taken'}
          >
            {submitting
              ? photoUploading
                ? 'در حال آپلود تصویر...'
                : 'در حال ثبت...'
              : isEdit
              ? 'ذخیره تغییرات'
              : 'ثبت بازیکن'}
          </GradientButton>
        </div>
      </form>
    </Modal>
  );
}